import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { changeAiSettings, deleteProvider, disableAi, enableAi, getAiState, getProviderInfo, testProviderConnection, getAiConfiguration } from '@api/ai-enterprise';
import { formatApiError } from '@api/common/errors';
import queryClient from '@app/common/queries/query-client';
import { getMutationAlertMessages, getQueryAlertMessages } from '@app/common/queries/formatters/alerts';
import { invalidateAllQueries } from '@common/queries/functions';
import { DEFAULT_POLLING_INTERVAL } from '@common/queries/constants';
import serverConfig from '@common/server-config-context/server-config';
export const keys = {
    all: ['ai'],
    status: ['ai', 'ai-enabled'],
    availableProviders: ['ai', 'providers'],
    provider: (provider) => ['ai', { provider }]
};
const aiConstituentQuery = {
    queryKey: keys.status,
    queryFn: getAiState
};
export function useAiConstituent() {
    const offlineModeEnabled = serverConfig.isOfflineMode();
    return useQuery({
        ...aiConstituentQuery,
        meta: getQueryAlertMessages(error => formatApiError(error, 'Failed to fetch AI Enterprise status')),
        staleTime: DEFAULT_POLLING_INTERVAL,
        refetchInterval: state => {
            return (state === null || state === void 0 ? void 0 : state.status) === 'enabled' && state.data.activeUsersCount === 0
                ? DEFAULT_POLLING_INTERVAL
                : false;
        },
        enabled: !offlineModeEnabled
    });
}
export function useAiEnableMutation(onSuccess) {
    return useMutation({
        mutationFn: enableAi,
        onSuccess: async (newAiEnabledState) => {
            queryClient.setQueryData(keys.status, newAiEnabledState);
            await invalidateAllQueries();
            onSuccess(newAiEnabledState);
        },
        meta: getMutationAlertMessages(error => formatApiError(error, 'Failed to enable AI Enterprise'), () => 'AI Enterprise enabled')
    });
}
export function useAiDisableMutation(onSuccess) {
    return useMutation({
        mutationFn: disableAi,
        onSuccess: async (newAiEnabledState) => {
            queryClient.setQueryData(keys.status, newAiEnabledState);
            await queryClient.invalidateQueries();
            onSuccess();
        },
        meta: getMutationAlertMessages(error => formatApiError(error, 'Failed to disable AI Enterprise'), () => 'AI Enterprise disabled')
    });
}
export function fetchAiEnabled() {
    return queryClient.fetchQuery({
        queryKey: keys.status,
        queryFn: getAiState,
        meta: getQueryAlertMessages(error => formatApiError(error, 'Failed to fetch AI Enterprise status'))
    });
}
export function ensureAiConstituentQueryData() {
    return queryClient.ensureQueryData({
        ...aiConstituentQuery,
        meta: getQueryAlertMessages(error => formatApiError(error, 'Failed to fetch AI Enterprise status'))
    });
}
export function ensureAiConfiguration() {
    return queryClient.ensureQueryData({
        queryKey: keys.availableProviders,
        queryFn: getAiConfiguration,
        meta: getQueryAlertMessages(error => formatApiError(error, 'Failed to fetch available providers'))
    });
}
export function useUpdateAiConfigurationMutation(newAiConfiguration, migrations) {
    return useMutation({
        mutationFn: async () => {
            const currentState = await queryClient.ensureQueryData({ ...aiConstituentQuery });
            if (currentState.status !== 'enabled') {
                throw new Error('AI Enterprise is not enabled');
            }
            const currentProviders = editableLLMProviders(currentState.data.provider).reduce((acc, value) => ({ ...acc, [value.id]: value }), {});
            const resultProviders = editableLLMProviders(newAiConfiguration.provider).reduce((acc, value) => ({ ...acc, [value.id]: value }), {});
            // we should create only new providers or update exist ones to make migration possible
            // deletion will be performed by migrations
            const updatedUpdateRequest = {
                ...newAiConfiguration,
                provider: { ...currentProviders, ...resultProviders }
            };
            await changeAiSettings(updatedUpdateRequest);
            await Promise.all(migrations.map(migration => deleteProvider(migration.provider, migration.type === 'migrate' ? migration.targetProvider : undefined)));
            return queryClient.fetchQuery({ ...aiConstituentQuery });
        },
        meta: getMutationAlertMessages(error => formatApiError(error, 'Failed to update AI configuration'), () => 'AI Enterprise settings changed')
    });
}
export function useProviderInfos(providers) {
    return useQueries({
        queries: providers.map(provider => ({
            queryFn: () => getProviderInfo(provider),
            queryKey: keys.provider(provider),
            meta: getQueryAlertMessages(error => formatApiError(error, 'Failed to fetch provider data')),
            select: (data) => ({
                provider,
                activeIdeProfilesCount: data.activeIdeProfilesCount
            })
        }))
    });
}
export function useTestProviderConnection() {
    return useMutation({
        mutationFn: async (provider) => testProviderConnection(provider),
        meta: getMutationAlertMessages(error => formatApiError(error, 'Failed to test provider connection'))
    });
}
export function useAiConfiguration(enabled = true) {
    return useQuery({
        queryKey: keys.availableProviders,
        queryFn: () => getAiConfiguration(),
        meta: getQueryAlertMessages(error => formatApiError(error, 'Failed to fetch available providers')),
        enabled
    });
}
export function editableLLMProviders(provider) {
    return (Object.values(provider)
        .filter(Boolean)
        // fixme: remove this with IDES-7911
        .filter(provider => provider.id !== 'mellum'));
}
